"use client"
import React, { useEffect, useRef } from "react"

/**
 * Warning: This component is **fragile**. Please test it in all modern browsers if you make **any** changes to it.
 *
 * Measures the offset that a regular vertical scrollbar creates and sets the value in the `--scrollbar-width` css variable in the root element.
 *
 * The size of the scrollbar can vary based on the browser, operating system, accessibility settings,
 * whether a mouse or trackpad is used, and more. The size can also change at any time...
 *
 * Maybe this component can be replaced by a pure css solution some time in the future, when CSS container queries are more mature.
 *
 * The logic is inpsired by this: https://stackoverflow.com/a/13382873 .
 */
export const ScrollbarWidthCalculator = () => {
  const innerRef = useRef<HTMLDivElement>()
  const outerRef = useRef<HTMLDivElement>()

  useEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) {
      return
    }
    let currentWidth = outer.offsetWidth - inner.offsetWidth
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${currentWidth}px`,
    )
    const resizeObserver = new ResizeObserver(() => {
      const newWidth = outer.offsetWidth - inner.offsetWidth
      if (newWidth !== currentWidth) {
        currentWidth = newWidth
        const scrollbarWidth = `${newWidth}px`
        document.documentElement.style.setProperty(
          "--scrollbar-width",
          scrollbarWidth,
        )
      }
    })

    resizeObserver.observe(outer, { box: "border-box" })

    return () => {
      resizeObserver.unobserve(outer)
    }
  }, [])

  return (
    <div
      aria-hidden
      ref={outerRef}
      className="pointer-events-none invisible fixed left-0 top-0 -z-[9999] h-0 max-h-0 min-h-0 w-max min-w-0 overflow-scroll"
    >
      <div ref={innerRef} className="h-px min-h-px w-px min-w-px" />
    </div>
  )
}
